import { computed, observable, type IComputedValue, type IObservableValue } from 'mobx';
import { toast } from 'react-toastify';

import { getNodeInstanceWaitEvents, type WaitEvent } from 'api/node_instances/operations/getNodeInstanceWaitEvents';
import { isRaiseable, raiseWaitEvent } from 'api/events/endpoints/raiseWaitEvent';
import { toError } from 'helpers/toError';

/**
 * The same event definition can appear twice — once per source (node snapshot
 * vs flow-child path). Raising is non-idempotent and `raised` is a global
 * per-event_id predicate, so only the FIRST raiseable occurrence of an eventId
 * owns the Raise action; later duplicates share its raised state and would
 * otherwise double-insert, so they get no button.
 */
const primaryActionIndices = (events: readonly WaitEvent[]): ReadonlySet<number> => {
  const seen = new Set<number>();
  const primary = new Set<number>();
  events.forEach((event, index) => {
    if (!isRaiseable(event) || event.eventId === null) return;
    if (seen.has(event.eventId)) return;
    seen.add(event.eventId);
    primary.add(index);
  });
  return primary;
};

/**
 * MobX-backed behaviour for the Wait Events tab. Owns the wait-event list, its
 * load/refresh lifecycle, and the per-row / bulk raise actions (firing toasts
 * directly, like EditModel). The component is a pure `observer` that builds one
 * of these per node instance and renders its state — all the testable logic
 * (dedup-by-eventId, raise + auto-refresh, busy tracking, bulk tally) lives
 * here and needs no React to exercise.
 *
 * Raise-all confirmation is intentionally NOT here: confirming is a dialog the
 * view renders. Individual raises don't confirm at all, matching the events
 * overview page's Raise action.
 */
export class WaitEventsModel {
  private readonly _nodeInstanceId: number | string | null;

  private readonly _events = observable.box<readonly WaitEvent[]>([], {
    deep: false,
  });
  private readonly _loading = observable.box(false);
  private readonly _refreshing = observable.box(false);
  private readonly _raisingAll = observable.box(false);
  private readonly _error: IObservableValue<string | null> = observable.box<string | null>(null);
  // eventIds with a raise in flight — disables that row's button (and the
  // duplicate rows it stands in for, since they share the eventId).
  private readonly _busyIds = observable.box<ReadonlySet<number>>(new Set(), {
    deep: false,
  });

  private readonly _primary: IComputedValue<ReadonlySet<number>>;
  private readonly _pending: IComputedValue<readonly WaitEvent[]>;

  constructor(nodeInstanceId: number | string | null) {
    this._nodeInstanceId = nodeInstanceId;
    this._primary = computed(() => primaryActionIndices(this._events.get()));
    this._pending = computed(() => this._events.get().filter((e, i) => this._primary.get().has(i) && !e.raised));
  }

  get events(): readonly WaitEvent[] {
    return this._events.get();
  }

  get isLoading(): boolean {
    return this._loading.get();
  }

  get isRefreshing(): boolean {
    return this._refreshing.get();
  }

  get isRaisingAll(): boolean {
    return this._raisingAll.get();
  }

  /** True while a refresh or a bulk raise is in flight — gates the toolbar. */
  get isBusy(): boolean {
    return this._refreshing.get() || this._raisingAll.get();
  }

  get error(): string | null {
    return this._error.get();
  }

  /** Not-yet-raised events that own their Raise action (deduped by eventId). */
  get pending(): readonly WaitEvent[] {
    return this._pending.get();
  }

  /** Whether the row at `index` owns the Raise action for its event. */
  canRaise(index: number): boolean {
    return this._primary.get().has(index);
  }

  /** Whether the event on this row (by eventId) has a raise in flight. */
  isRowBusy(event: WaitEvent): boolean {
    return event.eventId !== null && this._busyIds.get().has(event.eventId);
  }

  /** Initial load — drives the spinner. Kicked off by the view on mount. */
  load = async (): Promise<void> => {
    if (this._nodeInstanceId === null || this._nodeInstanceId === undefined) {
      return;
    }
    this._loading.set(true);
    await this.fetch();
    this._loading.set(false);
  };

  /** Manual refresh — same fetch, drives the toolbar's spinner instead. */
  refresh = async (): Promise<void> => {
    this._refreshing.set(true);
    await this.fetch();
    this._refreshing.set(false);
  };

  raise = async (event: WaitEvent): Promise<void> => {
    if (event.eventId === null) return;
    const eventId = event.eventId;
    this.setBusy(eventId, true);
    try {
      await raiseWaitEvent(event);
      toast.success(`Raised "${event.eventName}".`);
      await this.fetch();
    } catch (err) {
      toast.error(toError(err).message);
    } finally {
      this.setBusy(eventId, false);
    }
  };

  raiseAll = async (): Promise<void> => {
    const pending = this._pending.get();
    if (pending.length === 0) return;

    this._raisingAll.set(true);
    let raised = 0;
    const failures: string[] = [];
    // Sequential: small N, and it avoids firing a burst of raises at once.
    for (const event of pending) {
      try {
        await raiseWaitEvent(event);
        raised += 1;
      } catch (err) {
        failures.push(`${event.eventName}: ${toError(err).message}`);
      }
    }
    if (raised > 0) {
      toast.success(`Raised ${raised} event${raised === 1 ? '' : 's'}.`);
    }
    if (failures.length > 0) {
      toast.error(`Failed to raise ${failures.length}: ${failures.join('; ')}`);
    }
    this._raisingAll.set(false);
    await this.fetch();
  };

  private fetch = async (): Promise<void> => {
    if (this._nodeInstanceId === null || this._nodeInstanceId === undefined) {
      return;
    }
    this._error.set(null);
    try {
      this._events.set(await getNodeInstanceWaitEvents(this._nodeInstanceId));
    } catch (err) {
      console.error(err);
      this._error.set('Failed to load wait events.');
    }
  };

  private setBusy(eventId: number, busy: boolean): void {
    const next = new Set(this._busyIds.get());
    if (busy) {
      next.add(eventId);
    } else {
      next.delete(eventId);
    }
    this._busyIds.set(next);
  }
}
